import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  toggleTheme,
  THEME_OPTIONS,
  themeHelpers,
} from "../../store/slices/themeSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ShoppingCart,
  Menu,
  X,
  Moon,
  Sun,
  Monitor,
  ChevronLeft,
  Home,
  Grid3X3,
  Package,
  Info,
  Phone,
  Tag,
  Heart,
} from "lucide-react";
import {
  fadeInScale,
  iconHover,
  navItemHover,
  itemVariants,
  containerVariants,
} from "../../animations";
import NotificationsBar from "../Notifications";
import SearchOverlay from "../Search";
import { useCartContext } from "../../hooks/useCart";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, effectiveTheme } = useSelector((state) => state.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use cart hook
  const { cartCount, cartTotal } = useCartContext();

  const navItems = [
    { href: "/", label: "الرئيسية", icon: Home },
    { href: "/categories", label: "الفئات", icon: Grid3X3 },
    { href: "/products", label: "المنتجات", icon: Package },
    { href: "/sales", label: "العروض", icon: Tag },
    { href: "/about", label: "عن المتجر", icon: Info },
    { href: "/contact", label: "اتصل بنا", icon: Phone },
  ];

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {}, [effectiveTheme]);

  // Animation variants
  const headerVariants = {
    expanded: {
      height: "5rem",
      transition: { duration: 0.3, ease: "easeOut" },
    },
    compact: {
      height: "3.5rem",
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  const logoVariants = {
    expanded: {
      scale: 1,
      marginRight: "0rem",
    },
    compact: {
      scale: 0.9,
      marginRight: "2rem",
    },
  };

  const desktopNavVariants = {
    expanded: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
    compact: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  };

  const compactMenuVariants = {
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    open: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  // Get theme icon based on current theme
  const getThemeIcon = () => {
    switch (theme) {
      case THEME_OPTIONS.LIGHT:
        return <Sun className="w-4 h-4" />;
      case THEME_OPTIONS.DARK:
        return <Moon className="w-4 h-4" />;
      case THEME_OPTIONS.SYSTEM:
        return <Monitor className="w-4 h-4" />;
      default:
        return <Sun className="w-4 h-4" />;
    }
  };

  // theme toggle handler with immediate sync
  const handleThemeToggle = () => {
    // Dispatch Redux action - themeSlice will handle the DOM updates
    dispatch(toggleTheme());

    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  // Get theme indicator color
  const getThemeIndicatorColor = () => {
    switch (theme) {
      case THEME_OPTIONS.SYSTEM:
        return "var(--accent-sapphire)";
      case THEME_OPTIONS.DARK:
        return "var(--primary-400)";
      case THEME_OPTIONS.LIGHT:
        return "var(--accent-gold)";
      default:
        return "var(--primary-500)";
    }
  };

  // Handle cart click
  const handleCartClick = () => {
    navigate("/cart");
  };

  // Handle wishlist click
  const handleWishlistClick = () => {
    navigate("/wishlist");
  };

  return (
    <>
      <motion.header
        className="sticky top-0 z-50 transition-all duration-300"
        initial="expanded"
        animate={isScrolled ? "compact" : "expanded"}
        variants={headerVariants}
        style={{
          background: isScrolled
            ? "var(--bg-glass)"
            : effectiveTheme === "dark"
              ? "var(--secondary-950)"
              : "var(--bg-primary)",
          backdropFilter: "var(--backdrop-blur-lg)",
          borderBottom: "var(--glass-border)",
          boxShadow: isScrolled ? "var(--shadow-lg)" : "none",
        }}
      >
        <div className="container mx-auto px-4 h-full">
          {/* Main Navigation Bar */}
          <motion.div
            className="flex items-center justify-between h-full"
            variants={containerVariants}
          >
            {/* Logo and Navigation Area */}
            <motion.div
              className="flex items-center flex-1"
              variants={desktopNavVariants}
            >
              {/* Logo */}
              <motion.div
                className="flex items-center"
                style={{ paddingLeft: "1rem" }}
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link to="/">
                  <motion.div
                    className="flex items-center space-x-3 space-x-reverse p-2 rounded-2xl transition-all duration-300 cursor-pointer group relative overflow-hidden"
                    whileHover={{
                      scale: 1.05,
                      background: "var(--gradient-glass)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    variants={logoVariants}
                    style={{
                      background: "var(--bg-glass)",
                      backdropFilter: "var(--backdrop-blur)",
                      border: "var(--glass-border)",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    {/* Logo Image Container */}
                    <div className="relative w-8 h-8 rounded-xl overflow-hidden">
                      <motion.img
                        src="/logo.png"
                        alt="بروتاكـتا"
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      />
                      {/* Glass overlay effect on hover */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl transition-colors duration-300"
                        style={{
                          background: "var(--bg-glass)",
                        }}
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>

                    <motion.h1
                      className="text-xl font-extrabold transition-colors duration-300"
                      style={{
                        background: "var(--gradient-primary)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                      }}
                      whileHover={{
                        background: "var(--gradient-premium)",
                        backgroundClip: "text",
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      بروتاكـتا
                    </motion.h1>

                    {/* Subtle glow effect */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-colors duration-300"
                      style={{
                        background: "var(--gradient-overlay)",
                      }}
                      transition={{ duration: 0.4 }}
                    />
                  </motion.div>
                </Link>
              </motion.div>

              {/* Navigation Area */}
              <div className="flex-1 ml-6">
                <AnimatePresence mode="wait">
                  {!isScrolled ? (
                    <motion.div
                      key="notifications"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="h-full hidden md:block"
                    >
                      <NotificationsBar />
                    </motion.div>
                  ) : (
                    // Navigation Items when scrolled
                    <motion.nav
                      key="navigation"
                      className="hidden lg:flex items-center space-x-6 px-2 space-x-reverse h-full"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {navItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="transition-all duration-200 relative py-1 group flex items-center space-x-2 space-x-reverse text-sm font-medium"
                          style={{
                            color: "var(--text-secondary)",
                          }}
                        >
                          {item.icon == Phone ? (
                            <item.icon
                              className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                              style={{
                                color: "var(--text-secondary)",
                                transform: "rotate(260deg)",
                              }}
                            />
                          ) : (
                            <item.icon
                              className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                              style={{
                                color: "var(--text-secondary)",
                              }}
                            />
                          )}

                          <span className="group-hover:text-primary-500 transition-colors duration-200">
                            {item.label}
                          </span>
                          <motion.div
                            className="absolute bottom-0 right-0 w-0 h-0.5 rounded-full transition-colors duration-300"
                            style={{
                              background: "var(--gradient-primary)",
                            }}
                            whileHover={{ width: "100%" }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                          />
                        </Link>
                      ))}
                    </motion.nav>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Action Icons */}
            <motion.div
              className="flex items-center space-x-3 space-x-reverse"
              variants={itemVariants}
            >
              {/* Search Button */}
              <motion.button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 rounded-xl transition-all duration-200 relative group"
                style={{
                  background: "var(--bg-glass)",
                  backdropFilter: "var(--backdrop-blur)",
                }}
                variants={iconHover}
                whileHover="hover"
                whileTap="tap"
                aria-label="بحث"
              >
                <Search className="w-5 h-5" />
              </motion.button>

              {/* Wishlist */}
              <motion.button
                onClick={handleWishlistClick}
                className="p-2 rounded-xl transition-all duration-200 relative group hidden md:block"
                style={{
                  background: "var(--bg-glass)",
                  backdropFilter: "var(--backdrop-blur)",
                }}
                variants={iconHover}
                whileHover="hover"
                whileTap="tap"
                aria-label="المفضلة"
              >
                <Heart className="w-5 h-5" />
              </motion.button>

              {/* Shopping Cart */}
              <motion.button
                onClick={handleCartClick}
                className="p-2 rounded-xl transition-all duration-200 relative group"
                style={{
                  background: "var(--bg-glass)",
                  backdropFilter: "var(--backdrop-blur)",
                }}
                variants={iconHover}
                whileHover="hover"
                whileTap="tap"
                aria-label="سلة التسوق"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <motion.span
                    className="absolute -top-1 -left-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg transition-colors duration-300"
                    style={{
                      background: "var(--gradient-primary)",
                      boxShadow: "var(--shadow-glow)",
                    }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 15,
                    }}
                    key={`cart-${cartCount}`}
                  >
                    {cartCount > 9 ? "9+" : cartCount}
                  </motion.span>
                )}

                {/* Cart total tooltip on hover */}
                {cartCount > 0 && (
                  <motion.div
                    className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 rounded-md text-xs font-medium opacity-0 pointer-events-none whitespace-nowrap"
                    style={{
                      background: "var(--bg-primary)",
                      color: "var(--text-primary)",
                      boxShadow: "var(--shadow-md)",
                      border: "1px solid var(--border-primary)",
                    }}
                    whileHover={{ opacity: 1, y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    {cartTotal.toFixed(2)} ج.م
                  </motion.div>
                )}
              </motion.button>

              {/* Theme Toggle */}
              <motion.button
                onClick={handleThemeToggle}
                className="p-2 rounded-xl transition-all duration-200 group relative"
                variants={iconHover}
                whileHover="hover"
                whileTap="tap"
                title={`الوضع: ${themeHelpers.getThemeDisplayName(theme)} - التالي: ${themeHelpers.getThemeDisplayName(themeHelpers.getNextTheme(theme))}`}
                style={{
                  background: "var(--bg-glass)",
                  backdropFilter: "var(--backdrop-blur)",
                }}
                aria-label="تبديل الوضع"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={theme}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: 180 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {getThemeIcon()}
                  </motion.div>
                </AnimatePresence>

                {/* Theme indicator dot */}
                <motion.div
                  className="absolute -top-1 -right-1 w-2 h-2 rounded-full transition-colors duration-300"
                  style={{
                    background: getThemeIndicatorColor(),
                    boxShadow: "var(--shadow-glow)",
                  }}
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                {/* Theme cycle indicator */}
                <motion.div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300"
                  style={{
                    background: "var(--gradient-overlay)",
                  }}
                  whileHover={{
                    opacity: 1,
                  }}
                />
              </motion.button>

              {/* Mobile Menu Toggle */}
              <motion.button
                className="lg:hidden p-2 rounded-xl transition-all duration-200"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                variants={iconHover}
                whileHover="hover"
                whileTap="tap"
                style={{
                  color: isMenuOpen
                    ? "var(--text-on-primary)"
                    : "var(--text-secondary)",
                  background: isMenuOpen
                    ? "var(--gradient-primary)"
                    : "var(--bg-glass)",
                  backdropFilter: "var(--backdrop-blur)",
                }}
                aria-label="القائمة"
              >
                <AnimatePresence mode="wait">
                  {isMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0, rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X className="w-5 h-5" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ scale: 0, rotate: 90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0, rotate: -90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Menu className="w-5 h-5" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Desktop Navigation Menu */}
          <AnimatePresence>
            {!isScrolled && (
              <motion.nav
                className="hidden lg:block transition-colors duration-300"
                style={{
                  borderTop: "var(--glass-border)",
                }}
                initial="visible"
                animate="visible"
                variants={containerVariants}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="flex items-center justify-between py-3"
                  variants={containerVariants}
                >
                  <motion.div
                    className="flex items-center space-x-8 space-x-reverse"
                    variants={containerVariants}
                  >
                    {navItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="transition-all duration-200 relative py-2 group flex items-center space-x-2 space-x-reverse text-sm font-medium"
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      >
                        {item.icon == Phone ? (
                          <item.icon
                            className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                            style={{
                              color: "var(--text-secondary)",
                              transform: "rotate(260deg)",
                            }}
                          />
                        ) : (
                          <item.icon
                            className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                            style={{
                              color: "var(--text-secondary)",
                            }}
                          />
                        )}
                        <span className="group-hover:text-primary-500 transition-colors duration-200">
                          {item.label}
                        </span>
                        <motion.div
                          className="absolute bottom-0 right-0 w-0 h-0.5 rounded-full transition-colors duration-300"
                          style={{
                            background: "var(--gradient-primary)",
                          }}
                          whileHover={{ width: "100%" }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        />
                      </Link>
                    ))}
                  </motion.div>

                  {/* Additional Navigation Items */}
                  <motion.div
                    className="flex items-center space-x-6 space-x-reverse text-sm"
                    variants={containerVariants}
                  >
                    <Link
                      to="/sales"
                      className="font-semibold px-3 py-1 rounded-full transition-all duration-200"
                      style={{
                        color: "var(--error-500)",
                        background: "var(--error-50)",
                        backdropFilter: "var(--backdrop-blur)",
                      }}
                    >
                      عروض خاصة
                    </Link>
                    <Link
                      to="/request"
                      className="font-semibold px-3 py-1 rounded-full transition-all duration-200"
                      style={{
                        color: "var(--success-500)",
                        background: "var(--success-50)",
                        backdropFilter: "var(--backdrop-blur)",
                      }}
                    >
                      طلب بيع
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.nav>
            )}
          </AnimatePresence>

          {/* Mobile Navigation Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.nav
                className="lg:hidden overflow-hidden transition-colors duration-300"
                style={{
                  minHeight: "max-content",
                  minWidth: "100%",
                  background: "var(--gradient-primary)",
                  backdropFilter: "var(--backdrop-blur-lg)",
                  borderTop: "var(--glass-border)",
                  borderRadius: "12px",
                  marginTop: "8px",
                }}
                variants={compactMenuVariants}
                initial="closed"
                animate="open"
                exit="closed"
              >
                <motion.div className="py-4 space-y-2 px-4">
                  {/* Main Navigation Items */}
                  {navItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-200 group"
                      style={{
                        color: "var(--text-secondary)",
                        background: "var(--bg-secondary)",
                      }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <item.icon
                          className="w-5 h-5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                          style={{
                            color: "var(--text-secondary)",
                          }}
                        />
                        <span className="font-medium group-hover:text-primary-500 transition-colors duration-200">
                          {item.label}
                        </span>
                      </div>
                      <ChevronLeft
                        className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200"
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      />
                    </Link>
                  ))}

                  {/* Cart Item in Mobile Menu */}
                  <Link
                    to="/cart"
                    className="flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-200 group"
                    style={{
                      color: "var(--text-secondary)",
                      background: "var(--bg-secondary)",
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <ShoppingCart
                        className="w-5 h-5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      />
                      <div>
                        <span className="font-medium group-hover:text-primary-500 transition-colors duration-200 block">
                          سلة التسوق
                        </span>
                        <span className="text-xs opacity-70">
                          {cartCount} منتج • {cartTotal.toFixed(2)} ج.م
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {cartCount > 0 && (
                        <span
                          className="text-xs font-bold px-2 py-1 rounded-full text-white"
                          style={{
                            background: "var(--gradient-primary)",
                          }}
                        >
                          {cartCount}
                        </span>
                      )}
                      <ChevronLeft
                        className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200"
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      />
                    </div>
                  </Link>

                  {/* Wishlist Item in Mobile Menu */}
                  <Link
                    to="/wishlist"
                    className="flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-200 group"
                    style={{
                      color: "var(--text-secondary)",
                      background: "var(--bg-secondary)",
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <Heart
                        className="w-5 h-5 opacity-70 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110"
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      />
                      <span className="font-medium group-hover:text-primary-500 transition-colors duration-200">
                        المفضلة
                      </span>
                    </div>
                    <ChevronLeft
                      className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity duration-200"
                      style={{
                        color: "var(--text-secondary)",
                      }}
                    />
                  </Link>

                  {/* Additional Items in Mobile */}
                  <div
                    className="pt-4 mt-2 space-y-2 transition-colors duration-300"
                    style={{
                      borderTop: "1px solid var(--border-primary)",
                    }}
                  >
                    <Link
                      to="/sales"
                      className="flex items-center justify-center font-semibold py-2 px-4 rounded-xl transition-all duration-200"
                      style={{
                        color: "var(--error-500)",
                        background: "var(--error-50)",
                      }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      عروض خاصة
                    </Link>
                    <Link
                      to="/request"
                      className="flex items-center justify-center font-semibold py-2 px-4 rounded-xl transition-all duration-200"
                      style={{
                        color: "var(--success-500)",
                        background: "var(--success-50)",
                      }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      طلب بيع
                    </Link>
                  </div>
                </motion.div>
              </motion.nav>
            )}
          </AnimatePresence>
        </div>
      </motion.header>

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </>
  );
};

export default Navbar;
